<template>
  <section class="section-container">
    <h1 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
    <div class="section-container-body fade-up-animation">
      <p>Welcome to Vortec!</p>

      <p>
        These Terms and Conditions govern your use of the Vortec website, located at vortec.io, and any services offered
        through it. By accessing or using this website, you confirm that you have read, understood, and agree to be
        bound by these terms.
      </p>

      <p>
        Throughout these Terms, "you" and "your" refer to you, the visitor or user of this website. "Vortec", "we",
        "our", and "us" refer to Vortec. These Terms apply to all visitors, users, and anyone who accesses our website.
      </p>

      <p><strong>Last updated:</strong> February 2025</p>

      <h4 class="heading-4">Use of Website</h4>

      <p>
        This website is provided for informational purposes about Vortec's services. You agree to use it only for lawful
        purposes and in a way that does not infringe the rights of others or restrict their use of the website.
      </p>

      <p>You must not use this website to:</p>

      <ul style="margin-left: 20px">
        <li>Transmit any unsolicited or unauthorized advertising or promotional material</li>
        <li>Attempt to gain unauthorized access to any part of the website or its related systems</li>
        <li>Engage in any conduct that is unlawful, fraudulent, or harmful</li>
        <li>Transmit any data that contains viruses, malware, or other malicious code</li>
      </ul>

      <h4 class="heading-4">Cookies</h4>

      <p>
        We use cookies on vortec.io to improve your experience and to understand how our website is used. By continuing
        to use our website after accepting our cookie notice, you agree to our use of cookies in accordance with our
        <nuxt-link to="/cookie-policy">Cookie Policy</nuxt-link>. You may withdraw your consent at any time by clearing
        your cookies or adjusting your browser settings.
      </p>

      <h4 class="heading-4">Intellectual Property</h4>

      <p>
        Unless otherwise stated, Vortec owns all intellectual property rights in the content and materials published on
        vortec.io, including but not limited to text, graphics, logos, and code. All rights are reserved.
      </p>

      <p>
        You may view and access the content on vortec.io for personal, non-commercial use. You must not, without our
        prior written consent:
      </p>

      <ul style="margin-left: 20px">
        <li>Republish or reproduce material from vortec.io</li>
        <li>Sell, rent, or sub-license material from vortec.io</li>
        <li>Redistribute content from vortec.io in any form</li>
        <li>Use Vortec's name, logo, or branding without a separate trademark license agreement</li>
      </ul>

      <h4 class="heading-4">Linking to Our Website</h4>

      <p>
        You may link to vortec.io provided that the link is not deceptive, does not imply any form of endorsement or
        affiliation by Vortec, and is appropriate in the context of your website. We reserve the right to request
        removal of any link at our discretion.
      </p>

      <p>
        You may not create frames around our web pages that alter their visual presentation or appearance in any way
        without our prior written permission.
      </p>

      <h4 class="heading-4">Third-Party Links</h4>

      <p>
        Our website may contain links to third-party websites for your convenience or reference. These links do not
        imply endorsement of the linked sites, and Vortec has no control over and accepts no responsibility for the
        content, privacy practices, or availability of those sites.
      </p>

      <h4 class="heading-4">Disclaimer of Warranties</h4>

      <p>
        The content on vortec.io is provided on an "as is" and "as available" basis. While we endeavor to keep
        information accurate and up to date, we make no warranties or representations of any kind — express or implied —
        regarding the accuracy, completeness, reliability, or suitability of the content for any particular purpose.
      </p>

      <p>
        We do not guarantee that the website will be available at all times or that it will be free from errors or
        viruses.
      </p>

      <h4 class="heading-4">Limitation of Liability</h4>

      <p>
        To the fullest extent permitted by applicable law, Vortec shall not be liable for any direct, indirect,
        incidental, consequential, or punitive damages arising from your use of, or inability to use, this website or
        its content. This includes but is not limited to loss of data, revenue, or business opportunities.
      </p>

      <p>Nothing in these Terms limits or excludes liability for:</p>

      <ul style="margin-left: 20px">
        <li>Death or personal injury caused by negligence</li>
        <li>Fraud or fraudulent misrepresentation</li>
        <li>Any other liability that cannot be excluded or limited under applicable law</li>
      </ul>

      <h4 class="heading-4">Confidentiality & NDAs</h4>

      <p>
        Where a Non-Disclosure Agreement (NDA) or confidentiality agreement is entered into between Vortec and a client,
        the terms of that agreement take precedence with respect to confidential information shared in the course of our
        engagement. We are happy to sign NDAs prior to any project discussions upon request.
      </p>

      <h4 class="heading-4">Governing Law</h4>

      <p>
        These Terms and Conditions are governed by and construed in accordance with the laws of North Macedonia. Any
        disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the
        courts of North Macedonia, unless otherwise agreed in writing.
      </p>

      <h4 class="heading-4">Changes to These Terms</h4>

      <p>
        We reserve the right to update these Terms and Conditions at any time. Changes will be published on this page
        with a revised "Last updated" date. Your continued use of the website following any changes constitutes your
        acceptance of the updated Terms.
      </p>

      <h4 class="heading-4">Contact Us</h4>

      <p>
        If you have any questions about these Terms and Conditions, please contact us at
        <a href="mailto:hello@vortec.io">hello@vortec.io</a>.
      </p>
    </div>
  </section>
  <ClientOnly>
    <ContactForm />
  </ClientOnly>
</template>

<script setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';

const lines = [
  { text: 'Terms and', special: false },
  { text: 'Conditions', special: true },
];

useFadeUpObserver();

useSeo({
  title: 'Terms & Conditions | VORTEC',
  description: 'Learn about our terms and conditions and how they apply to your use of our website.',
  path: '/terms-and-conditions',
  jsonLd: {
    '@type': 'WebPage',
    name: 'Terms & Conditions | VORTEC',
    description: 'Learn about our terms and conditions and how they apply to your use of our website.',
    url: 'https://vortec.io/terms-and-conditions',
  },
});
</script>
